import { Check, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { JinjaText } from '@/components/JinjaText'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { SigningFact } from './model'

export function FactPicker({
  facts,
  value,
  label,
  disabled,
  className,
  invalid,
  onChange,
}: {
  facts: SigningFact[]
  value: string
  label: string
  disabled: boolean
  className: string
  invalid: { 'aria-invalid'?: boolean; 'aria-describedby'?: string }
  onChange: (fact: SigningFact) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
          className={className}
          {...invalid}>
          {value ? (
            <JinjaText
              text={`{${value}}`}
              className="block min-w-0 flex-1 truncate whitespace-nowrap [&_.jinja-token]:inline! [&_.jinja-token]:whitespace-nowrap!"
            />
          ) : (
            <span className="flex-1 text-muted-foreground">Choose a fact</span>
          )}
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto p-0">
        <Command label="Find a fact">
          <CommandInput placeholder="Find a fact…" aria-label="Find a fact" />
          <CommandList className="max-h-52">
            <CommandEmpty>No matching Facts.</CommandEmpty>
            {facts.map((fact) => (
              <CommandItem
                key={fact.binding}
                value={fact.binding}
                keywords={[fact.label]}
                onSelect={() => {
                  onChange(fact)
                  setOpen(false)
                }}>
                <div className="min-w-0 flex-1">
                  <JinjaText
                    text={`{${fact.binding}}`}
                    className="block truncate"
                  />
                  <span className="block truncate text-xs text-muted-foreground">
                    {fact.label}
                  </span>
                </div>
                {value === fact.binding && (
                  <Check className="size-4 shrink-0" />
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
